import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import {
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuthStore } from "./contexts/useAuthStore.ts";
import { RootLayout } from "./components/layout/RootLayout.tsx";
import { createResourceRoutes } from "@/shared/routing";

// Lazy imports
const HomePage = lazy(() => import("@/views/HomePage.tsx"));
const Clienti = lazy(
  () => import("@/views/anagrafiche/clienti/ClientiView.tsx"),
);
const DomandeVerbale = lazy(
  () => import("@/views/assist/domande-verbale/DomandeVerbaleView.tsx"),
);
const LoginPage = lazy(() => import("@/pages/LoginPage.tsx"));
const ParcoNoleggio = lazy(
  () => import("@/views/parco/parco-noleggio/ParcoNoleggioView.tsx"),
);
const RapportiniAssistenza = lazy(
  () =>
    import("@/views/assist/rapportini-assistenza/RapportiniAssistenzaView.tsx"),
);
const Report = lazy(
  () => import("@/views/reports/silenti/ReportSilentiView.tsx"),
);
const ChangeOver = lazy(
  () => import("@/views/parco/change-over/ChangeOverView.tsx"),
);
const Riparazioni = lazy(
  () => import("@/views/parco/riparazioni/RiparazioniView.tsx"),
);
const Logistica = lazy(
  () => import("@/views/manutenzione/logistica/LogisticaView.tsx"),
);
const Riferimenti = lazy(
  () => import("@/views/anagrafiche/riferimenti/RiferimentiView.tsx"),
);
const Utenti = lazy(() => import("@/views/anagrafiche/utenti/UtentiView.tsx"));
const Agenti = lazy(() => import("@/views/anagrafiche/agenti/AgentiView.tsx"));
const ReportPresenze = lazy(
  () => import("@/views/reports/presenze/ReportPresenzeView.tsx"),
);
const ReportGiacenza = lazy(
  () => import("@/views/reports/giacenza/ReportGiacenzaView.tsx"),
);
const Raggruppamenti = lazy(
  () => import("@/views/raggruppamenti/RaggruppamentiView.tsx"),
);

const Categorie = lazy(
  () => import("@/views/parco/categorie/CategorieView.tsx"),
);

const CollegamentiMacchine = lazy(
  () =>
    import("@/views/parco/collegamenti-macchine/CollegamentiMacchineView.tsx"),
);

const CollegamentiAccessori = lazy(
  () =>
    import("@/views/parco/collegamenti-accessori/CollegamentiAccessoriView.tsx"),
);
const VociDocumento = lazy(
  () => import("@/views/parco/voci-documento/VociDocumentoView.tsx"),
);
const CollegamentiVoci = lazy(
  () =>
    import("@/views/parco/collegamenti-voci-parco/CollegamentiVociParcoView.tsx"),
);
const CollegamentiVociTipoMacchina = lazy(
  () =>
    import("@/views/parco/collegamenti-voci-tipo-macchina/CollegamentiVociTipoMacchinaView.tsx"),
);
const RegistrazioneVendita = lazy(
  () =>
    import("@/views/vendite/registrazione-vendita/RegistrazioneVenditaView.tsx"),
);

const ReportContratti = lazy(
  () => import("@/views/reports/contratti/ReportContrattiView.tsx"),
);

const Preventivi = lazy(
  () => import("@/views/noleggio/preventivi/PreventiviView.tsx"),
);
const Contratti = lazy(
  () => import("@/views/noleggio/contratti/ContrattiView.tsx"),
);
const RapportiniFatti = lazy(
  () =>
    import("@/views/assist/preparazione/rapportini-fatti/RapportiniFattiView.tsx"),
);

const RapportiniDaFare = lazy(
  () =>
    import("@/views/assist/preparazione/rapportini-da-fare/RapportiniDaFareView.tsx"),
);
const MobileApp = lazy(() => import("@/mobile/MobileApp.tsx"));

const MezziUsoInterno = lazy(
  () => import("@/views/anagrafiche/mezzi/MezziUsoInternoView.tsx"),
);

const RegistroGasolio = lazy(
  () => import("@/views/assist/registro-gasolio/RegistroGasolioView.tsx"),
);

const Filiali = lazy(
  () => import("@/views/anagrafiche/filiali/FilialiView.tsx"),
);

const MappaClienti = lazy(
  () => import("@/views/anagrafiche/mappa-clienti/MappaClientiView.tsx"),
);

const MonitoringPlus = lazy(
  () =>
    import("@/views/anagrafiche/monitoring-plus/MonitoringPlusView.tsx"),
);

export const App: React.FC = () => {
  const { isAuthenticated, loading } = useAuthStore();
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down("md"));

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      }
    >
      <Routes>
        {!isAuthenticated ? (
          <Route path="*" element={<LoginPage />} />
        ) : isMobileDevice ? (
          <Route path="*" element={<MobileApp />} />
        ) : (
          <Route element={<RootLayout />}>
            <Route path="/" element={<HomePage />} />

            {/* CRUD resources: /path, /path/new, /path/:id[, /path/:id/clone] */}
            {createResourceRoutes("/parco-noleggio", <ParcoNoleggio />, {
              withClone: true,
            })}
            {createResourceRoutes("/clienti", <Clienti />)}
            {createResourceRoutes("/filiali", <Filiali />, { withClone: true })}
            {createResourceRoutes("/mezzi-uso-interno", <MezziUsoInterno />, {
              withClone: true,
            })}
            {createResourceRoutes(
              "/assistenza-rapportini",
              <RapportiniAssistenza />,
              { withClone: true },
            )}
            {createResourceRoutes("/change-over", <ChangeOver />)}
            {createResourceRoutes("/riparazioni", <Riparazioni />, {
              withClone: true,
            })}
            {createResourceRoutes("/domande-verbale", <DomandeVerbale />, {
              withClone: true,
            })}
            {createResourceRoutes("/logistica", <Logistica />)}
            {createResourceRoutes("/rapportini-fatti", <RapportiniFatti />)}
            {createResourceRoutes("/rapportini-da-fare", <RapportiniDaFare />)}
            {createResourceRoutes("/riferimenti", <Riferimenti />, {
              withClone: true,
            })}
            {createResourceRoutes("/utenti", <Utenti />, { withClone: true })}
            {createResourceRoutes("/agenti", <Agenti />, { withClone: true })}
            {createResourceRoutes("/raggruppamenti", <Raggruppamenti />, {
              withClone: true,
            })}
            {createResourceRoutes("/categorie", <Categorie />, {
              withClone: true,
              extra: (
                <Route path="/categorie/:id/listini" element={<Categorie />} />
              ),
            })}
            {createResourceRoutes(
              "/collegamenti-macchine",
              <CollegamentiMacchine />,
              { withClone: true },
            )}
            {createResourceRoutes(
              "/collegamenti-accessori",
              <CollegamentiAccessori />,
              { withClone: true },
            )}
            {createResourceRoutes("/collegamenti-voci", <CollegamentiVoci />, {
              withClone: true,
            })}
            {createResourceRoutes(
              "/collegamenti-voci-tipo-macchina",
              <CollegamentiVociTipoMacchina />,
              { withClone: true },
            )}
            {createResourceRoutes("/voci-documento", <VociDocumento />, {
              withClone: true,
            })}
            {createResourceRoutes(
              "/registrazione-vendita",
              <RegistrazioneVendita />,
            )}
            {createResourceRoutes("/preventivi", <Preventivi />, {
              withClone: true,
            })}
            {createResourceRoutes("/contratti", <Contratti />, {
              withClone: false,
            })}
            {createResourceRoutes("/registro-gasolio", <RegistroGasolio />)}

            {/* Table / map only — no nested form routes */}
            <Route path="/mappa-clienti" element={<MappaClienti />} />
            <Route path="/monitoring-plus" element={<MonitoringPlus />} />
            <Route path="/report-silenti" element={<Report />} />
            <Route path="/report-presenze" element={<ReportPresenze />} />
            <Route path="/report-giacenza" element={<ReportGiacenza />} />
            <Route path="/report-contratti" element={<ReportContratti />} />

            <Route path="*" element={<h1>Ops!</h1>} />
          </Route>
        )}
      </Routes>
    </Suspense>
  );
};

export default App;
